import { useRollbar } from '@rollbar/react'
import { FileSourceEnum } from '@systemeio/file-manager'
import {
  DateTimePicker,
  FormInput,
  FormInputSearch,
  FormSelect,
  Hint,
  Popover,
  Tooltip,
  toast,
} from '@systemeio/ui'
import {
  BLOGS_API,
  createPost,
  deletePost,
  duplicatePost,
  updatePost,
} from 'modules/blogs/api/blog-api'
import BlogCategoriesCheckbox from 'modules/blogs/components/blog-categories-checkbox'
import { SchedulePostModal } from 'modules/blogs/components/schedule-post-modal'
import { useBlogPosts } from 'modules/blogs/hooks/use-blog-posts'
import { BlogEntityDuplicateInterface } from 'modules/blogs/types/blog-entity-interface'
import { BlogInterface } from 'modules/blogs/types/blog-interface'
import {
  BlogPostId,
  BlogPostsCreateInterface,
  BlogPostsEditInterface,
  BlogPostsErrorsInterface,
  BlogPostsFilterInterface,
  BlogPostsInterface,
} from 'modules/blogs/types/blog-posts-interface'
import { useEffect, useState } from 'react'
import { ImageUploaderWithPreviewImage } from 'shared/components/file-uploader/image-uploader-with-preview-image'
import FormUrlPathInput from 'shared/components/form-url-path-input'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { buildPath } from 'shared/utils/build-path'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { useBlogPostApi } from '../../hooks/use-blog-post-api'
import { ActivatePostModal } from '../activet-post-modal'
import BlogEntityDuplicateModal from '../blog-entity-duplicate-modal'
import { defaultErrors, defaultFilters, defaultValues } from './constants'
import { CheckCircleIcon, QuestionMarkIcon, TimeCircleIcon } from '@systemeio/ui/icons'

interface BlogPostsProps {
  blog?: BlogInterface
  blogId: string
}
const BlogPosts = ({ blog, blogId }: BlogPostsProps) => {
  const { t } = useLocoTranslation()
  const rollbar = useRollbar()

  const { user, intlLocale } = useUser()
  const { setPagination, pagination, setLimit, limit } = usePagination(true)
  const [filter, setFilter] = useState<BlogPostsFilterInterface>(defaultFilters)
  const [errors, setErrors] = useState<BlogPostsErrorsInterface>(defaultErrors)
  const { blogPosts, blogPostsMutate, blogPostsCounter } = useBlogPosts({
    limit,
    pagination,
    id: blogId,
    filter,
  })

  const { changePostStatus } = useBlogPostApi()

  const updateData = useUpdateData({
    api: `${BLOGS_API}/${blogId}/posts`,
    setPagination,
  })
  const isActiveData: { id: string; caption: string }[] = [
    { id: 'false', caption: t('global.inactive') },
    { caption: t('global.active'), id: 'true' },
  ]

  const onCreate = async (data: BlogPostsCreateInterface) => {
    try {
      await fetchWithErrors(async () => {
        if (blog) {
          const newData: BlogPostsEditInterface = {
            ...data,
            categories: data.categories?.map(el => el.id),
            image: data.image?.id || null,
          }
          await createPost(blog?.id, newData)
          await updateData(true, blogPosts)
        }
      }, setErrors)
      toast.success(t('dashboard.blog.posts.created'))
    } catch (e) {
      throw e
    }
  }
  const onEdit = async (post: BlogPostsInterface, data: BlogPostsInterface) => {
    try {
      await fetchWithErrors(async () => {
        if (blog) {
          const newData: BlogPostsEditInterface = {
            name: data.name,
            path: data.path,
            description: data.description,
            categories: data.categories.map(el => el.id),
            image: data.image?.id || null,
            postedAt: data?.postedAt,
            activatedAt: data?.activatedAt,
          }
          const response = await updatePost(post.id, newData)
          await updateData(false, undefined, {
            mutationData: blogPosts,
            onMutate: async () => {
              await blogPostsMutate(data => {
                if (data) {
                  return {
                    ...data,
                    items: data.items.map(el => {
                      if (el.id === post.id) {
                        return { ...response.data }
                      } else {
                        return el
                      }
                    }),
                  }
                }
              }, false)
            },
          })
        }
      }, setErrors)
    } catch (e) {
      throw e
    }
  }

  useEffect(() => {
    if (blogPosts) {
      blogPosts.items.map(blog => {
        if (!blog.categories) {
          rollbar.info(`Categories not found in  blog`, {
            data: JSON.stringify(blog),
          })
        }
      })
    }
  }, [blogPosts])

  const onRemove = async (blogPost: BlogPostsInterface) => {
    try {
      await deletePost(blogPost.id)
      await updateData(false, blogPosts)
    } catch (e) {
      throw e
    }
  }

  const duplicateHandler = async (id: number, blogPost: BlogEntityDuplicateInterface) => {
    await duplicatePost(id, blogPost)
    await updateData(false, blogPosts)
  }

  const onSchedulePost = async (postId: BlogPostId, post: BlogPostsInterface) => {
    await updateData(false, undefined, {
      mutationData: blogPosts,
      onMutate: async () => {
        await blogPostsMutate(data => {
          if (data) {
            return {
              ...data,
              items: data.items.map(el => {
                if (el.id === postId) {
                  return post
                }
                return el
              }),
            }
          }
        }, false)
      },
    })
  }

  const onChangeStatus = async (blogPost: BlogPostsInterface) => {
    const update = blogPost.isActive ? 'deactivate' : 'activate'
    await changePostStatus(blogPost.id, update)
    await updateData(false, undefined, {
      mutationData: blogPosts,
      onMutate: async () => {
        await blogPostsMutate(data => {
          if (data) {
            return {
              items: data.items.map(item => {
                if (item.id === blogPost.id) {
                  item = { ...item, isActive: !item.isActive }
                  if (!item.isActive) item.activatedAt = null
                }
                return item
              }),
              hasMore: data.hasMore,
            }
          }
        }, false)
      },
    })
  }

  const onModalClose = () => setErrors(defaultErrors)

  const columns: ColumnDefinitionInterface<BlogPostsInterface>[] = [
    {
      key: 'name',
      header: t('global.title_caption'),
      widthPercent: 40,
      onRender: blogPost => (
        <div className="truncate">
          <a rel="noreferrer" className="primary-link w-full" href={blogPost.editorPageUrl}>
            {blogPost.name}
          </a>
        </div>
      ),
    },
    {
      key: 'category',
      header: t('dashboard.blog.categories.title_plural'),
      widthPercent: 20,
      onRender: blogPost => (
        <ul className={'flex w-full flex-col gap-1.5'}>
          {blogPost.categories?.map(el => (
            <li className={'truncate'} key={el.id}>
              {el.name}
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'isActive',
      header: t('global.status'),
      widthPercent: 15,
      onRender: blogPost => (
        <div className="flex items-center lg:ml-4">
          {blogPost.activatedAt ? (
            <>
              {blogPost.isActive ? (
                <Tooltip
                  label={blogPost.isActive ? t('global.active') : t('global.inactive')}
                  mode="hover"
                  key="status-tooltip"
                >
                  <CheckCircleIcon
                    className={`fill-green h-[16px] w-[16px] shrink-0 sm:h-[20px] sm:w-[20px]`}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  label={dateTimeFormat(blogPost.activatedAt, {
                    timeFormat: user?.timeFormat,
                    dateFormat: user?.dateFormat,
                    timeZone: user?.timezone,
                  })}
                  mode="hover"
                  key="status-tooltip"
                >
                  <TimeCircleIcon
                    className={`fill-yellow h-[16px] w-[16px] shrink-0 sm:h-[20px] sm:w-[20px]`}
                  />
                </Tooltip>
              )}
            </>
          ) : (
            <>
              <Tooltip
                label={blogPost.isActive ? t('global.active') : t('global.inactive')}
                mode="hover"
                key="status-tooltip"
              >
                <CheckCircleIcon
                  className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${
                    blogPost.isActive && 'fill-green'
                  }`}
                />
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('global.created_on'),
      widthPercent: 25,
      onRender: blogPost => (
        <span>
          {dateTimeFormat(blogPost.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]
  return (
    <ListingLayout
      title={t('dashboard.blog.posts.title_plural')}
      counter={blogPostsCounter}
      data={blogPosts}
      type={'compact'}
      filterData={{
        limiter: { limit, setLimit },
        paginator: { pagination, setPagination },
        filterer: { filter, setFilter, defaultFilters },
        filterable: [
          {
            field: 'isActive',
            onRender: (value, onChange) => (
              <FormSelect
                key={'status-filter'}
                data={isActiveData}
                value={value}
                placeholder={t('dashboard.blog.posts.all_posts')}
                onChange={data => onChange(data)}
              />
            ),
          },
          {
            field: 'searchBy',
            onRender: (value, onChange) => (
              <FormInputSearch
                key={'searchBy-filter'}
                onClear={() => onChange('')}
                value={value}
                onChange={e => onChange(e.target.value)}
              />
            ),
          },
        ],
      }}
      createData={{
        onCreate: onCreate,
        onAfterLeave: onModalClose,
        requiredFields: ['name', 'description', 'path'],
        fullCaption: t('dashboard.blog.posts.create_new_blog_post'),
        needToast: false,
        defaultValues,
        creatable: [
          {
            field: 'name',
            onRender: (value, onChange, state, onChangeState) => (
              <FormInput
                required
                key={'name-create'}
                error={errors.name}
                value={value}
                label={
                  <div className={'flex items-center gap-1'}>
                    {t('global.title_caption')}
                    <Popover
                      popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                      label={t('dashboard.blog.posts.title_description')}
                    >
                      {show => (
                        <QuestionMarkIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                        />
                      )}
                    </Popover>
                  </div>
                }
                onChange={e => {
                  onChangeState({
                    ...state,
                    name: e.target.value,
                    path: buildPath(e.target.value),
                  })
                }}
              />
            ),
          },
          {
            field: 'description',
            onRender: (value, onChange) => (
              <FormInput
                required
                key={'description-create'}
                error={errors.description}
                value={value}
                label={
                  <div className={'flex items-center gap-1'}>
                    {t('global.short_description')}
                    <Popover
                      popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                      label={t('dashboard.blog.posts.short_description_description')}
                    >
                      {show => (
                        <QuestionMarkIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                        />
                      )}
                    </Popover>
                  </div>
                }
                onChange={e => onChange(e.target.value)}
              />
            ),
          },
          {
            field: 'path',
            onRender: (value, onChange) => (
              <FormUrlPathInput
                required
                key={'path-create'}
                value={value}
                error={errors.path}
                domainName={blog?.domain.domainName}
                additionalPath={blog?.path}
                label={
                  <div className={'flex items-center gap-1'}>
                    {t('global.url_path')}
                    <Popover
                      popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                      label={t('dashboard.funnel.configuration.webinar.url_path_popover-2')}
                    >
                      {show => (
                        <QuestionMarkIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                        />
                      )}
                    </Popover>
                  </div>
                }
                onChange={e => onChange(e.target.value)}
              />
            ),
          },
          {
            field: 'image',
            onRender: (value, onChange) => (
              <ImageUploaderWithPreviewImage
                key={'image-create'}
                value={value}
                label={
                  <Hint label={t('dashboard.blog.posts.image_description')}>
                    {t('global.image')}
                  </Hint>
                }
                previewClassName="h-[300px] w-[300px]"
                error={errors.image}
                source={FileSourceEnum.BlogPost}
                aspectRatio={10 / 7}
                onRemove={() => onChange(null)}
                onSelect={dataFile => onChange(dataFile)}
              />
            ),
          },
          {
            field: 'categories',
            onRender: (value, onChange) => (
              <BlogCategoriesCheckbox
                blogId={blogId}
                key={'categories-create'}
                selectedCategories={value}
                onChange={data => onChange(data)}
                error={errors.categories}
              />
            ),
          },
        ],
      }}
    >
      <Table
        caption={t('dashboard.blog.posts.title')}
        actions={{
          openLink: [
            {
              name: 'view',
              openInNew: true,
              setHref: blogPost =>
                blogPost.isActive
                  ? `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${blog?.domain.domainName}${
                      blog?.path !== '' ? `/${blog?.path}` : ''
                    }${blogPost.path !== '' ? `/${blogPost.path}` : ''}`
                  : `/blog/post/${blogPost.id}/generate-preview`,
            },
            {
              caption: t('dashboard.actions.title.edit'),
              name: 'edit',
              openInNew: false,
              setHref: blogPost => blogPost.editorPageUrl,
            },
          ],
          custom: [
            {
              name: 'duplicate',
              caption: t('dashboard.actions.title.duplicate'),
              onActionRender: (opened, onClose, post, key) => (
                <BlogEntityDuplicateModal
                  key={key}
                  opened={opened}
                  blogEntity={post}
                  title={t('dashboard.blog.page.duplicate_blog_post')}
                  onClose={onClose}
                  onUpdateData={async () => {
                    onModalClose()
                    onClose()
                  }}
                  duplicateHandler={duplicateHandler}
                />
              ),
            },
            {
              name: 'Schedule',
              caption: t('dashboard.blog.posts.schedule'),
              onActionRender: (opened, onClose, post, key) => (
                <SchedulePostModal
                  key={key}
                  opened={opened}
                  onClose={onClose}
                  post={post}
                  onSchedulePost={onSchedulePost}
                />
              ),
            },
            {
              name: 'isActive',
              caption: t('dashboard.actions.title.activate'),
              shouldRender: blogPost => blogPost.isActive === false,
              onActionRender: (opened, onClose, post, key) => (
                <ActivatePostModal
                  key={key}
                  opened={opened}
                  onClose={onClose}
                  post={post}
                  onChangeStatus={onChangeStatus}
                  onSchedulePost={onSchedulePost}
                />
              ),
            },
          ],

          settings: {
            onEdit: onEdit,
            toastCaption: t('dashboard.blog.posts.updated'),
            fullCaption: t('dashboard.blog.posts.edit_posts'),
            onModalClose,
            editable: [
              {
                field: 'name',
                onRender: (value, onChange) => (
                  <FormInput
                    required
                    key={'name-edit'}
                    error={errors.name}
                    value={value}
                    label={
                      <div className={'flex items-center gap-1'}>
                        {t('global.title_caption')}
                        <Popover
                          popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                          label={t('dashboard.blog.posts.title_description')}
                        >
                          {show => (
                            <QuestionMarkIcon
                              className={`${
                                show ? 'fill-brand-primary' : 'fill-fill-primary'
                              } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                            />
                          )}
                        </Popover>
                      </div>
                    }
                    onChange={e => onChange(e.target.value)}
                  />
                ),
              },
              {
                field: 'description',
                onRender: (value, onChange) => (
                  <FormInput
                    required
                    key={'description-edit'}
                    error={errors.description}
                    value={value}
                    label={
                      <div className={'flex items-center gap-1'}>
                        {t('global.short_description')}
                        <Popover
                          popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                          label={t('dashboard.blog.posts.short_description_description')}
                        >
                          {show => (
                            <QuestionMarkIcon
                              className={`${
                                show ? 'fill-brand-primary' : 'fill-fill-primary'
                              } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                            />
                          )}
                        </Popover>
                      </div>
                    }
                    onChange={e => onChange(e.target.value)}
                  />
                ),
              },
              {
                field: 'path',
                onRender: (value, onChange) => (
                  <FormUrlPathInput
                    required
                    key={'path-edit'}
                    value={value}
                    error={errors.path}
                    domainName={blog?.domain.domainName}
                    additionalPath={blog?.path}
                    label={
                      <div className={'flex items-center gap-1'}>
                        {t('global.url_path')}
                        <Popover
                          popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                          label={t('dashboard.funnel.configuration.webinar.url_path_popover-2')}
                        >
                          {show => (
                            <QuestionMarkIcon
                              className={`${
                                show ? 'fill-brand-primary' : 'fill-fill-primary'
                              } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                            />
                          )}
                        </Popover>
                      </div>
                    }
                    onChange={e => onChange(e.target.value)}
                  />
                ),
              },
              {
                field: 'postedAt',
                onRender: (value, onChange) => (
                  <div key={'postedAt'} className={'flex flex-col gap-1'}>
                    <div className={'flex items-center gap-1'}>
                      {t('dashboard.blog.posts.post_publication_date.title')}
                      <Popover
                        popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                        label={t('dashboard.blog.posts.post_publication_date.popover')}
                      >
                        {show => (
                          <QuestionMarkIcon
                            className={`${
                              show ? 'fill-brand-primary' : 'fill-fill-primary'
                            } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                          />
                        )}
                      </Popover>
                    </div>
                    <DateTimePicker
                      error={errors.postedAt}
                      value={value}
                      onChange={value => onChange(value)}
                      createdAt={user?.createdAt}
                      timezone={user?.timezone}
                      intlLocale={intlLocale}
                    />
                  </div>
                ),
              },
              {
                field: 'image',
                onRender: (value, onChange) => (
                  <ImageUploaderWithPreviewImage
                    key={'image-edit'}
                    value={value}
                    label={
                      <Hint label={t('dashboard.blog.posts.image_description')}>
                        {t('global.image')}
                      </Hint>
                    }
                    previewClassName="h-[300px] w-[300px]"
                    error={errors.image}
                    source={FileSourceEnum.BlogPost}
                    aspectRatio={10 / 7}
                    onRemove={() => onChange(null)}
                    onSelect={file => onChange(file)}
                  />
                ),
              },
              {
                field: 'categories',
                onRender: (value, onChange) => (
                  <BlogCategoriesCheckbox
                    blogId={blogId}
                    key={'categories-edit'}
                    selectedCategories={value}
                    onChange={data => onChange(data)}
                    error={errors.categories}
                  />
                ),
              },
            ],
          },
          activate: {
            customStatusField: 'isActive',
            onChange: blogPost => onChangeStatus(blogPost),
            shouldRender: blogPost => blogPost.isActive,
          },
          remove: { onChange: onRemove },
        }}
        data={blogPosts}
        columns={columns}
        paginator={{ pagination, setPagination }}
        limiter={{ limit, setLimit }}
      />
    </ListingLayout>
  )
}

export default BlogPosts
